/**
 * @file Contains a hook to get navigation items for the rail
 */

import { useLocation } from 'react-router-dom';

import useUnreadMessagesCount from '../../../../../containers/leftSidebar/useUnreadMessagesCount';
import useParamHotelId from '../../../../../hooks/router/params/useParamHotelId';
import useCanAccessChatbotLulu from '../../../../../hooks/useCanAccessChatbotLulu';
import useCanUserAccessReservations from '../../../../../hooks/useCanAccessReservations';
import translate from '../../../../../i18n/translate';
import { useIsChatEnabled } from '../../../../../integrations/roqChat/useIsChatEnabled';
import { getChatbotLuluUrl } from '../../../../../routes/urls/chatbot';
import { getDashboardUrl } from '../../../../../routes/urls/dashboard';
import { getReservationsUrl } from '../../../../../routes/urls/reservations';
import { getSearchUrl } from '../../../../../routes/urls/search';
import { getSidebarToggleUrl } from '../../../../../routes/urls/searchParams/sidebar';
import { getCanViewDashboard } from '../../../../../utils/permissions/dashboard';
import { NavItem } from '../../types';

/**
 * Generate navigation items for the rail
 *
 * @returns The items to show
 */
const useNavigationItems = (): NavItem[] => {
  const canAccessChatbotLulu = useCanAccessChatbotLulu();
  const canAccessReservations = useCanUserAccessReservations();
  const canViewDashboardPage = getCanViewDashboard();
  const hotelId = useParamHotelId();
  const isChatEnabled = useIsChatEnabled();
  const location = useLocation();
  const nrUnreadTopics = useUnreadMessagesCount();

  if (hotelId === null) {
    return [];
  }

  const items: NavItem[] = [];

  if (canViewDashboardPage) {
    items.push({
      icon: 'home',
      id: 'home',
      label: translate('GENERAL__HOME'),
      to: getDashboardUrl(hotelId),
    });
  }

  items.push({
    icon: 'topic',
    id: 'topics',
    label: translate('NAVIGATION__TOPICS'),
    notificationsCount: nrUnreadTopics,
    to: getSidebarToggleUrl(location, 'topics'),
  });

  items.push({
    icon: 'filter',
    id: 'smartViews',
    label: translate('GENERAL__SMART_VIEWS'),
    to: getSidebarToggleUrl(location, 'smartViews'),
  });

  // Show chat icon before chat permission initialization and remove if not enabled
  if (isChatEnabled !== false) {
    items.push({
      icon: 'chatBubble',
      id: 'chat',
      label: translate('GENERAL__CHAT'),
      to: getSidebarToggleUrl(location, 'chat'),
    });
  }

  items.push({
    icon: 'search',
    id: 'search',
    label: translate('NAVIGATION__SEARCH'),
    to: getSearchUrl(hotelId),
  });

  if (canAccessReservations) {
    items.push({
      icon: 'hotel',
      id: 'bookings',
      label: translate('NAVIGATION__RESERVATIONS'),
      to: getReservationsUrl(hotelId),
    });
  }

  if (canAccessChatbotLulu) {
    items.push({
      icon: 'chatbotLulu',
      id: 'chatbotLulu',
      label: translate('NAVIGATION__CHATBOT_LULU'),
      to: getChatbotLuluUrl(hotelId),
    });
  }

  return items;
};

export default useNavigationItems;
